import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Grid,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    EditOutlined,
    FilterOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const InventoryAdjustmentsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const canAdjust = ['ADMIN', 'MANAGER', 'STAFF'].includes(user?.role);
    const [filterForm] = Form.useForm();
    const [adjustForm] = Form.useForm();

    const [ledger, setLedger] = useState([]);
    const [items, setItems] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const adjustmentType = Form.useWatch('adjustment_type', adjustForm);
    const quantityDelta = Form.useWatch('quantity_delta', adjustForm);
    const correctedQuantity = Form.useWatch('corrected_quantity', adjustForm);

    const fetchFormData = async () => {
        try {
            const [itemsRes, branchesRes, locationsRes] = await Promise.all([
                client.get('/master/items'),
                client.get('/master/branches'),
                client.get('/master/locations'),
            ]);
            setItems(itemsRes.data || []);
            setBranches(branchesRes.data || []);
            setLocations(locationsRes.data || []);
        } catch {
            message.error('Failed to load adjustment filters');
        }
    };

    const fetchLedger = async () => {
        setLoading(true);
        try {
            const values = filterForm.getFieldsValue();
            const params = { exclude_adjustments: 1 };

            if (values.branch_id && values.branch_id !== 'all') params.branch_id = values.branch_id;
            if (values.location_id && values.location_id !== 'all') params.location_id = values.location_id;
            if (values.item_id && values.item_id !== 'all') params.item_id = values.item_id;
            if (values.transaction_type && values.transaction_type !== 'all') params.transaction_type = values.transaction_type;
            if (values.search) params.search = values.search;
            if (values.date_range?.length === 2) {
                params.start_date = values.date_range[0].format('YYYY-MM-DD');
                params.end_date = values.date_range[1].format('YYYY-MM-DD');
            }

            const { data } = await client.get('/inventory/ledger', { params });
            setLedger(data || []);
        } catch {
            message.error('Failed to load inventory entries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFormData();
        fetchLedger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const branchId = Form.useWatch('branch_id', filterForm);
    const filteredLocations = useMemo(() => {
        if (!branchId || branchId === 'all') return locations;
        return locations.filter((loc) => Number(loc.branch_id) === Number(branchId));
    }, [branchId, locations]);

    const transactionTypes = useMemo(() => {
        return Array.from(new Set((ledger || []).map((entry) => entry.transaction_type).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b));
    }, [ledger]);

    const adjustmentSummary = (entry) => {
        const adjustments = entry.adjustments || [];
        const totalDelta = adjustments.reduce((sum, adj) => sum + Number(adj.quantity_delta || 0), 0);
        return {
            count: adjustments.length,
            totalDelta,
            adjustedQuantity: Number(entry.quantity || 0) + totalDelta,
        };
    };

    const sourceReference = (entry) => {
        if (entry.batch_no) return entry.batch_no;
        if (entry.remarks) {
            const match = entry.remarks.match(/[-–]\s*(.+)$/);
            if (match) return match[1].trim();
        }
        return `Ledger #${entry.id}`;
    };

    const openAdjustmentModal = (entry) => {
        setSelectedEntry(entry);
        adjustForm.resetFields();
        adjustForm.setFieldsValue({ adjustment_type: 'DECREASE' });
        setModalOpen(true);
    };

    const preview = useMemo(() => {
        if (!selectedEntry) return null;

        const summary = adjustmentSummary(selectedEntry);
        let delta = 0;

        if (adjustmentType === 'INCREASE') {
            delta = Math.abs(Number(quantityDelta || 0));
        } else if (adjustmentType === 'DECREASE') {
            delta = -Math.abs(Number(quantityDelta || 0));
        } else if (adjustmentType === 'CORRECTION' && correctedQuantity !== undefined && correctedQuantity !== null) {
            delta = Number(correctedQuantity || 0) - summary.adjustedQuantity;
        }

        return {
            currentAdjustedQuantity: summary.adjustedQuantity,
            delta,
            resultingQuantity: summary.adjustedQuantity + delta,
        };
    }, [adjustmentType, correctedQuantity, quantityDelta, selectedEntry]);

    const submitAdjustment = async (values) => {
        if (!selectedEntry) return;

        setSubmitLoading(true);
        try {
            const payload = {
                inventory_ledger_id: selectedEntry.id,
                adjustment_type: values.adjustment_type,
                reason: values.reason,
            };

            if (values.adjustment_type === 'CORRECTION') {
                payload.corrected_quantity = values.corrected_quantity;
            } else {
                payload.quantity_delta = values.quantity_delta;
            }

            await client.post('/inventory/adjustments', payload);
            message.success('Adjustment posted');
            setModalOpen(false);
            setSelectedEntry(null);
            adjustForm.resetFields();
            fetchLedger();
        } catch (error) {
            const errors = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(' ')
                : '';
            message.error(error.response?.data?.message || errors || 'Failed to post adjustment');
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'date',
            width: 150,
            render: (date) => dayjs(date).format('MMM D, YYYY HH:mm'),
        },
        {
            title: 'Reference',
            key: 'reference',
            width: 160,
            render: (_, entry) => sourceReference(entry),
        },
        {
            title: 'Item',
            key: 'item',
            render: (_, entry) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{entry.item?.name || 'Item'}</Text>
                    <Text type="secondary">{entry.item?.unit?.abbreviation || entry.item?.unit?.name || 'unit'}</Text>
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'transaction_type',
            key: 'type',
            width: 150,
            render: (type) => <Tag color="blue">{type === 'IN' ? 'PURCHASE_IN' : type}</Tag>,
        },
        {
            title: 'Qty',
            key: 'quantity',
            width: 150,
            align: 'right',
            render: (_, entry) => {
                const summary = adjustmentSummary(entry);
                return (
                    <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                        <Text strong>{Number(entry.quantity || 0).toLocaleString()}</Text>
                        {summary.count > 0 && (
                            <Text type="secondary">Adjusted: {summary.adjustedQuantity.toLocaleString()}</Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Scope',
            key: 'scope',
            render: (_, entry) => (
                <Space direction="vertical" size={0}>
                    <Text>{entry.branch?.name || '-'}</Text>
                    <Text type="secondary">{entry.location?.name || '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Adjustments',
            key: 'adjustments',
            width: 140,
            render: (_, entry) => {
                const summary = adjustmentSummary(entry);
                if (!summary.count) return <Tag>None</Tag>;
                return <Tag color={summary.totalDelta >= 0 ? 'green' : 'red'}>{summary.count} ({summary.totalDelta.toLocaleString()})</Tag>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, entry) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EditOutlined />}
                    disabled={!canAdjust}
                    onClick={() => openAdjustmentModal(entry)}
                >
                    Adjust
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: isMobile ? 'flex-start' : 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Inventory Adjustments</Title>
                    <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Post linked corrections to inventory entries while preserving movement history.</Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={fetchLedger} style={{ height: 40, borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>
                    Refresh
                </Button>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: 20 }} style={{ marginBottom: 20 }}>
                <Form
                    form={filterForm}
                    layout="vertical"
                    initialValues={{ branch_id: 'all', location_id: 'all', item_id: 'all', transaction_type: 'all' }}
                    onFinish={fetchLedger}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={6}>
                            <Form.Item name="branch_id" label="Branch">
                                <Select
                                    options={[{ label: 'All branches', value: 'all' }, ...branches.map((branch) => ({ label: branch.name, value: branch.id }))]}
                                    onChange={() => filterForm.setFieldsValue({ location_id: 'all' })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="location_id" label="Location">
                                <Select options={[{ label: 'All locations', value: 'all' }, ...filteredLocations.map((loc) => ({ label: loc.name, value: loc.id }))]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="item_id" label="Item">
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={[{ label: 'All items', value: 'all' }, ...items.map((item) => ({ label: `${item.name} (${item.code})`, value: item.id }))]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="transaction_type" label="Movement Type">
                                <Select options={[{ label: 'All types', value: 'all' }, ...transactionTypes.map((type) => ({ label: type, value: type }))]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="date_range" label="Date Range">
                                <RangePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item name="search" label="Search">
                                <Input allowClear prefix={<SearchOutlined />} placeholder="Reference, lot, remarks, type, or ledger ID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label=" ">
                                <Space style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                                    <Button htmlType="submit" type="primary" icon={<FilterOutlined />} style={{ height: 40, borderRadius: 8, flex: isMobile ? 1 : undefined }}>
                                        Apply
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            filterForm.resetFields();
                                            fetchLedger();
                                        }}
                                        style={{ height: 40, borderRadius: 8, flex: isMobile ? 1 : undefined }}
                                    >
                                        Clear
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <Card className="premium-card" bodyStyle={{ padding: 0 }} title={<span style={{ fontWeight: 700 }}>Inventory Entries</span>}>
                <Table
                    columns={columns}
                    dataSource={ledger}
                    rowKey="id"
                    loading={loading}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                    scroll={isMobile ? { x: 1000 } : undefined}
                />
            </Card>

            <Modal
                title="Adjust Inventory Entry"
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setSelectedEntry(null);
                    adjustForm.resetFields();
                }}
                onOk={() => adjustForm.submit()}
                okText="Post Adjustment"
                confirmLoading={submitLoading}
                okButtonProps={{ style: { height: 40, borderRadius: 8, fontWeight: 600 }, icon: <PlusOutlined /> }}
                cancelButtonProps={{ style: { height: 40, borderRadius: 8 } }}
                width={620}
                destroyOnClose
            >
                {selectedEntry && (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Card size="small" bodyStyle={{ padding: 12 }}>
                            <Row gutter={[12, 8]}>
                                <Col xs={24} md={12}><Text type="secondary">Reference: </Text><Text strong>{sourceReference(selectedEntry)}</Text></Col>
                                <Col xs={24} md={12}><Text type="secondary">Item: </Text><Text strong>{selectedEntry.item?.name}</Text></Col>
                                <Col xs={24} md={12}><Text type="secondary">Original Qty: </Text><Text>{Number(selectedEntry.quantity || 0).toLocaleString()}</Text></Col>
                                <Col xs={24} md={12}><Text type="secondary">Current Adjusted Qty: </Text><Text>{preview?.currentAdjustedQuantity.toLocaleString()}</Text></Col>
                            </Row>
                        </Card>

                        <Form form={adjustForm} layout="vertical" onFinish={submitAdjustment}>
                            <Form.Item name="adjustment_type" label="Adjustment Type" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { label: 'Increase stock', value: 'INCREASE' },
                                        { label: 'Decrease stock', value: 'DECREASE' },
                                        { label: 'Correction - set adjusted entry quantity', value: 'CORRECTION' },
                                    ]}
                                />
                            </Form.Item>

                            {adjustmentType === 'CORRECTION' ? (
                                <Form.Item name="corrected_quantity" label="Corrected Entry Quantity" rules={[{ required: true, message: 'Enter corrected quantity' }]}>
                                    <InputNumber style={{ width: '100%', height: 40 }} precision={4} placeholder="0.0000" />
                                </Form.Item>
                            ) : (
                                <Form.Item name="quantity_delta" label="Quantity" rules={[{ required: true, message: 'Enter quantity' }]}>
                                    <InputNumber style={{ width: '100%', height: 40 }} min={0.0001} precision={4} placeholder="0.0000" />
                                </Form.Item>
                            )}

                            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Enter adjustment reason' }]}>
                                <Input.TextArea rows={3} placeholder="Reason for this entry-linked adjustment" />
                            </Form.Item>

                            <Card size="small" bodyStyle={{ padding: 12 }}>
                                <Row gutter={[12, 8]}>
                                    <Col xs={24} md={8}><Text type="secondary">Delta: </Text><Text strong>{Number(preview?.delta || 0).toLocaleString()}</Text></Col>
                                    <Col xs={24} md={8}><Text type="secondary">Resulting Entry Qty: </Text><Text strong>{Number(preview?.resultingQuantity || 0).toLocaleString()}</Text></Col>
                                    <Col xs={24} md={8}><Text type="secondary">Lot: </Text><Text>{selectedEntry.batch_no || '-'}</Text></Col>
                                </Row>
                            </Card>
                        </Form>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default InventoryAdjustmentsPage;
